import type { WorkOrderBomLine } from "./workOrdersPageModel";

export type WorkOrderDeviationBomLineChange = {
  componentSku: string;
  quantityPerFinishedGood: number;
  action: "add" | "update" | "remove";
};

export type WorkOrderDeviationRow = {
  rowKey: string;
  /** SKU on the work order when the dialog opened (null for added rows). */
  originalComponentSku: string | null;
  componentSku: string;
  quantityPerFinishedGood: string;
  /** Baseline qty / FG when the dialog opened (for "was" labels). */
  originalQuantityPerFinishedGood: string;
  quantityAllocated: number;
  quantityConsumed: number;
  isNew: boolean;
};

export type WorkOrderDeviationApplyPayload = {
  reason: string;
  newOrderedQuantity?: number;
  bomLineChanges?: WorkOrderDeviationBomLineChange[];
};

export type WorkOrderDeviationRecordPayload = {
  priorOrderedQuantity?: number;
  newOrderedQuantity?: number;
  bomLineChanges?: Array<{
    componentSku: string;
    quantityPerFinishedGood: number;
    action: string;
  }>;
};

const OPEN_HUB_STATUSES = new Set(["draft", "open", "in_process", "partially_received"]);

export const WORK_ORDER_DEVIATION_HELP =
  "Hub-native BOM change. Updates the work order BOM in the hub only; Megaventory is not rebuilt. Finished goods receive uses the updated BOM. Quantities per finished good apply to units not yet received. Set quantity per finished good to 0 to drop a component from the remaining build (the row stays visible so you can see the change). If a change needs less material than is already soft-allocated, return allocated material before allocate or receive.";

export function isOpenHubWorkOrderStatus(status: string | null | undefined): boolean {
  const normalized = (status ?? "").trim().toLowerCase();
  return normalized !== "" && OPEN_HUB_STATUSES.has(normalized);
}

export function workOrderDeviationDisabledReason(input: {
  hubStatus: string | null | undefined;
}): string | null {
  if (!isOpenHubWorkOrderStatus(input.hubStatus)) {
    return "BOM deviations are only available on open hub-native work orders.";
  }
  return null;
}

export function remainingFinishedGoodQuantity(orderedQuantity: number, receivedQuantity: number): number {
  return Math.max(0, Math.round(orderedQuantity) - Math.round(receivedQuantity));
}

export function deviationComponentRequiredQuantity(input: {
  quantityPerFinishedGood: number;
  remainingFinishedGoodQuantity: number;
  quantityConsumed?: number;
}): number {
  const consumed = Math.max(0, Math.round(Number(input.quantityConsumed ?? 0)));
  const remainingFg = Math.max(0, Math.round(input.remainingFinishedGoodQuantity));
  const perFg = Number(input.quantityPerFinishedGood);
  if (!Number.isFinite(perFg) || perFg < 0) {
    return consumed;
  }
  return consumed + Math.round(perFg * remainingFg);
}

export function effectivePerFgForDeviationLine(
  line: WorkOrderBomLine,
  remainingFinishedGoodQuantity: number,
): number {
  if (remainingFinishedGoodQuantity > 0 && line.quantityOrdered > 0) {
    return line.quantityOrdered / remainingFinishedGoodQuantity;
  }
  return line.quantityPerFinishedGood;
}

export function deviationRowsFromBom(
  bom: WorkOrderBomLine[],
  remainingFinishedGoodQuantity: number,
): WorkOrderDeviationRow[] {
  return bom.map((line, index) => {
    const perFg = formatDeviationQuantity(
      effectivePerFgForDeviationLine(line, remainingFinishedGoodQuantity),
    );
    return {
      rowKey: `bom-${line.id}-${index}`,
      originalComponentSku: line.componentSku.trim(),
      componentSku: line.componentSku.trim(),
      quantityPerFinishedGood: perFg,
      originalQuantityPerFinishedGood: perFg,
      quantityAllocated: line.quantityAllocated,
      quantityConsumed: line.quantityConsumed ?? 0,
      isNew: false,
    };
  });
}

export function formatDeviationQuantity(value: number): string {
  if (!Number.isFinite(value)) return "";
  const rounded = Math.round(value * 10000) / 10000;
  if (Math.abs(rounded - Math.trunc(rounded)) < 1e-9) {
    return String(Math.trunc(rounded));
  }
  return String(rounded);
}

export function parseDeviationQuantity(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

export function buildDeviationBomLineChanges(
  originalBom: WorkOrderBomLine[],
  rows: WorkOrderDeviationRow[],
  remainingFinishedGoodQuantity: number,
): WorkOrderDeviationBomLineChange[] {
  const originalBySku = new Map(
    originalBom.map((line) => [line.componentSku.trim().toLowerCase(), line]),
  );
  const changes: WorkOrderDeviationBomLineChange[] = [];
  const seenOriginalSkus = new Set<string>();

  for (const row of rows) {
    const sku = row.componentSku.trim();
    const originalSku = (row.originalComponentSku ?? "").trim();
    if (sku === "" && originalSku === "") continue;

    const parsedQty = parseDeviationQuantity(row.quantityPerFinishedGood);
    const originalKey = originalSku.toLowerCase();

    if (originalSku !== "" && sku !== "" && sku.toLowerCase() !== originalKey) {
      if (!seenOriginalSkus.has(originalKey)) {
        changes.push({ componentSku: originalSku, quantityPerFinishedGood: 0, action: "remove" });
        seenOriginalSkus.add(originalKey);
      }
      if (parsedQty != null && parsedQty > 0) {
        changes.push({ componentSku: sku, quantityPerFinishedGood: parsedQty, action: "add" });
      }
      continue;
    }

    const effectiveSku = sku !== "" ? sku : originalSku;
    if (effectiveSku === "") continue;
    const skuKey = effectiveSku.toLowerCase();
    if (seenOriginalSkus.has(skuKey)) continue;
    seenOriginalSkus.add(skuKey);

    const original = originalBySku.get(skuKey);

    if (original == null) {
      if (parsedQty != null && parsedQty > 0) {
        changes.push({ componentSku: effectiveSku, quantityPerFinishedGood: parsedQty, action: "add" });
      }
      continue;
    }

    if (parsedQty == null || parsedQty <= 0) {
      changes.push({ componentSku: effectiveSku, quantityPerFinishedGood: 0, action: "remove" });
      continue;
    }

    const baselinePerFg = effectivePerFgForDeviationLine(original, remainingFinishedGoodQuantity);
    if (parsedQty !== baselinePerFg) {
      changes.push({ componentSku: effectiveSku, quantityPerFinishedGood: parsedQty, action: "update" });
    }
  }

  for (const line of originalBom) {
    const skuKey = line.componentSku.trim().toLowerCase();
    if (!seenOriginalSkus.has(skuKey)) {
      changes.push({
        componentSku: line.componentSku.trim(),
        quantityPerFinishedGood: 0,
        action: "remove",
      });
    }
  }

  return changes;
}

export function deviationNeedsAllocationResolution(input: {
  originalBom: WorkOrderBomLine[];
  bomLineChanges: WorkOrderDeviationBomLineChange[];
  priorOrderedQuantity: number;
  newOrderedQuantity: number;
  receivedFinishedGoodQuantity: number;
}): boolean {
  const bomBySku = new Map(
    input.originalBom.map((line) => [line.componentSku.trim().toLowerCase(), line]),
  );
  const priorRemaining = remainingFinishedGoodQuantity(
    input.priorOrderedQuantity,
    input.receivedFinishedGoodQuantity,
  );
  const newRemaining = remainingFinishedGoodQuantity(
    input.newOrderedQuantity,
    input.receivedFinishedGoodQuantity,
  );

  for (const change of input.bomLineChanges) {
    const skuKey = change.componentSku.trim().toLowerCase();
    const line = bomBySku.get(skuKey);
    if (change.action === "remove") {
      const consumed = line?.quantityConsumed ?? 0;
      const allocated = line?.quantityAllocated ?? 0;
      if (allocated > consumed) return true;
      continue;
    }
    const consumed = line?.quantityConsumed ?? 0;
    const newRequired = deviationComponentRequiredQuantity({
      quantityPerFinishedGood: change.quantityPerFinishedGood,
      remainingFinishedGoodQuantity: newRemaining,
      quantityConsumed: consumed,
    });
    if (newRequired < (line?.quantityAllocated ?? 0)) return true;
  }

  if (input.bomLineChanges.length === 0 && input.newOrderedQuantity !== input.priorOrderedQuantity) {
    for (const line of input.originalBom) {
      const consumed = line.quantityConsumed ?? 0;
      const perFg =
        priorRemaining > 0
          ? line.quantityOrdered / priorRemaining
          : input.priorOrderedQuantity > 0
            ? line.quantityOrdered / input.priorOrderedQuantity
            : 0;
      const newRequired = deviationComponentRequiredQuantity({
        quantityPerFinishedGood: perFg,
        remainingFinishedGoodQuantity: newRemaining,
        quantityConsumed: consumed,
      });
      if (newRequired < line.quantityAllocated) return true;
    }
  }

  return false;
}

export function buildDeviationApplyPayload(input: {
  reason: string;
  originalBom: WorkOrderBomLine[];
  rows: WorkOrderDeviationRow[];
  priorOrderedQuantity: number;
  receivedFinishedGoodQuantity: number;
  newOrderedQuantityRaw: string;
}): WorkOrderDeviationApplyPayload | { error: string } {
  const reason = input.reason.trim();
  if (reason === "") {
    return { error: "Reason is required." };
  }

  const parsedOrdered = parseDeviationQuantity(input.newOrderedQuantityRaw);
  if (parsedOrdered == null || parsedOrdered <= 0) {
    return { error: "Ordered quantity must be a positive number." };
  }
  if (parsedOrdered < input.receivedFinishedGoodQuantity) {
    return {
      error: `Ordered quantity cannot be below ${input.receivedFinishedGoodQuantity} already received.`,
    };
  }

  const bomLineChanges = buildDeviationBomLineChanges(
    input.originalBom,
    input.rows,
    remainingFinishedGoodQuantity(input.priorOrderedQuantity, input.receivedFinishedGoodQuantity),
  );
  const orderedChanged = parsedOrdered !== input.priorOrderedQuantity;
  const hasBomChanges = bomLineChanges.length > 0;

  if (!orderedChanged && !hasBomChanges) {
    return { error: "Change the ordered quantity or at least one BOM line before applying a deviation." };
  }

  for (const row of input.rows) {
    const sku = row.componentSku.trim();
    const originalSku = (row.originalComponentSku ?? "").trim();
    if (sku === "" && originalSku === "" && row.isNew) {
      return { error: "Every new BOM line needs a component SKU." };
    }
    const effectiveSku = sku !== "" ? sku : originalSku;
    if (effectiveSku !== "" && parseDeviationQuantity(row.quantityPerFinishedGood) == null) {
      return { error: `Quantity per finished good is invalid for ${effectiveSku}.` };
    }
  }

  const payload: WorkOrderDeviationApplyPayload = { reason };
  if (orderedChanged) {
    payload.newOrderedQuantity = Math.round(parsedOrdered);
  }
  if (hasBomChanges) {
    payload.bomLineChanges = bomLineChanges;
  }
  return payload;
}

export function formatDeviationStatus(status: string): string {
  switch (status.trim()) {
    case "applied":
      return "Applied";
    case "pending_allocation_resolution":
      return "Pending allocation resolution";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

export function formatDeviationChangeHoverHint(payload: WorkOrderDeviationRecordPayload | undefined): string {
  if (payload == null) return "No change details recorded.";
  const lines: string[] = [];
  if (
    payload.priorOrderedQuantity != null &&
    payload.newOrderedQuantity != null &&
    payload.priorOrderedQuantity !== payload.newOrderedQuantity
  ) {
    lines.push(
      `Ordered finished-good quantity: ${payload.priorOrderedQuantity.toLocaleString()} → ${payload.newOrderedQuantity.toLocaleString()}`,
    );
  }
  for (const change of payload.bomLineChanges ?? []) {
    if (change.action === "remove") {
      lines.push(`Remove component ${change.componentSku}`);
      continue;
    }
    if (change.action === "add") {
      lines.push(
        `Add component ${change.componentSku} (${formatDeviationQuantity(change.quantityPerFinishedGood)} per finished good)`,
      );
      continue;
    }
    lines.push(
      `Update component ${change.componentSku} to ${formatDeviationQuantity(change.quantityPerFinishedGood)} per finished good`,
    );
  }
  if (lines.length === 0) {
    return "No BOM or quantity changes recorded in the payload.";
  }
  return lines.join("\n");
}
